import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, ChevronLeft, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Experience type options
const experienceTypes = [
  { id: "vip", label: "VIP Upgrade" },
  { id: "birthday", label: "Birthday" },
  { id: "bachelor", label: "Bachelor / Bachelorette" },
  { id: "group", label: "Large Group (8+)" },
  { id: "influencer", label: "Influencer / Brand" },
  { id: "other", label: "Other" },
];

// Package options
const packageOptions = [
  { id: "skipline", label: "Skip-the-line VIP" },
  { id: "tequila", label: "Private tequila tasting" },
  { id: "checkin", label: "Reserved group check-in" },
  { id: "bottle", label: "Bottle service partner venues" },
  { id: "custom", label: "Custom crawl coordination" },
  { id: "takeover", label: "Full VIP takeover" },
];

// Cities
const cities = [
  "Atlanta", "Austin", "Chicago", "Dallas", "Houston", 
  "Los Angeles", "Miami", "New York", "San Diego"
];

const VIPExperiences = () => {
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [message, setMessage] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    groupSize: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const togglePackage = (id) => {
    setSelectedPackages(prev => 
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !selectedCity || !selectedExperience) return;
    
    setLoading(true);
    setError("");
    
    try {
      const response = await fetch(`${API_URL}/api/vip-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          group_size: formData.groupSize || "Not specified",
          preferred_city: selectedCity,
          message: `Experience: ${selectedExperience}. Packages: ${selectedPackages.join(', ') || 'None'}. Note: ${message || 'None'}`
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('experience-grid')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <motion.div 
          className="text-center max-w-md"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C24E2C] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#D4AF37]/20">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h2 className="font-heading font-black text-4xl md:text-5xl uppercase mb-3" style={{ color: "#F4F1E8" }}>
            REQUEST <span className="text-terracotta">RECEIVED</span>
          </h2>
          <div className="w-16 h-0.5 mx-auto my-4" style={{ background: "#D4AF37" }} />
          <p className="text-white/50 text-lg mb-8">
            Our VIP team will reach out within 24–48 hours to build your experience.
          </p>
          <Link to="/">
            <Button className="bg-[#C9F400] hover:bg-[#D8FF40] text-[#0B0B0F] px-8 py-4 font-accent font-bold">
              BACK TO HOME
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="vip-experiences-page">
      {/* Back Navigation */}
      <Link 
        to="/" 
        className="fixed top-6 left-6 z-50 flex items-center gap-2 text-white/50 hover:text-white transition-colors"
        data-testid="back-to-home"
      >
        <ChevronLeft className="w-5 h-5" />
        <span className="font-accent text-sm uppercase tracking-wider">Back</span>
      </Link>

      {/* Hero Section */}
      <section 
        className="min-h-[70vh] flex flex-col items-center justify-center px-4 pt-16 pb-12 relative overflow-hidden"
        data-testid="vip-hero"
      >
        {/* Emerald vignette */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(15,59,46,0.15) 0%, transparent 70%)',
          }}
        />

        <motion.div 
          className="text-center max-w-4xl mx-auto relative z-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="mb-6">
            <span 
              className="block font-heading font-black text-5xl md:text-7xl lg:text-8xl uppercase tracking-tight leading-[0.85]"
              style={{ color: "#F4F1E8" }}
            >
              GO VIP.
            </span>
            <span 
              className="block font-heading font-black text-5xl md:text-7xl lg:text-8xl uppercase tracking-tight leading-[0.85]"
              style={{ color: "#C24E2C" }}
            >
              GO HARDER.
            </span>
          </h1>

          {/* Gold divider */}
          <div className="w-20 h-0.5 mx-auto my-8" style={{ background: "#D4AF37" }} />

          <p className="text-white/60 text-lg md:text-xl font-light mb-2">
            Birthdays. Big Groups. Special Occasions.
          </p>
          <p className="text-white/40 text-base md:text-lg font-light italic mb-10">
            Let's build your night.
          </p>

          <Button 
            onClick={scrollToForm}
            className="bg-[#C9F400] hover:bg-[#D8FF40] text-[#0B0B0F] px-10 py-6 font-accent font-bold text-base tracking-wider"
            data-testid="start-request-btn"
          >
            START YOUR REQUEST
          </Button>
        </motion.div>
      </section>

      {/* Experience Options Grid */}
      <section 
        id="experience-grid"
        className="py-16 md:py-24 px-4 relative"
        style={{ background: "#0A0A0A" }}
        data-testid="experience-grid-section"
      >
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 60% 40% at 50% 0%, rgba(194,78,44,0.04) 0%, transparent 60%)',
          }}
        />

        <div className="max-w-4xl mx-auto relative z-10">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-black text-3xl md:text-5xl uppercase tracking-tight mb-4" style={{ color: "#F4F1E8" }}>
              WHAT ARE YOU <span className="text-terracotta">CELEBRATING?</span>
            </h2>
            <div className="w-16 h-0.5 mx-auto" style={{ background: "#D4AF37" }} />
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
            {experienceTypes.map((exp, i) => (
              <motion.button
                key={exp.id}
                onClick={() => setSelectedExperience(exp.id)}
                className={`relative py-6 md:py-8 px-4 rounded-lg text-center transition-all duration-300 ${
                  selectedExperience === exp.id 
                    ? 'ring-2 ring-[#D4AF37]' 
                    : 'hover:bg-[#1A1A1A]'
                }`}
                style={{
                  background: selectedExperience === exp.id ? 'rgba(194,78,44,0.15)' : '#111111',
                  boxShadow: selectedExperience === exp.id 
                    ? '0 0 30px rgba(212,175,55,0.2)' 
                    : '0 4px 20px rgba(0,0,0,0.3)',
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                data-testid={`experience-${exp.id}`}
              >
                <span 
                  className={`font-accent font-bold text-sm md:text-base uppercase tracking-wider ${
                    selectedExperience === exp.id ? 'text-[#D4AF37]' : 'text-[#F4F1E8]'
                  }`}
                >
                  {exp.label}
                </span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* City Selector */}
      <section 
        className="py-14 md:py-20 px-4"
        style={{ background: "#050505", position: "relative", zIndex: 50 }}
        data-testid="city-selector-section"
      >
        <div className="max-w-md mx-auto" style={{ overflow: 'visible' }}>
          <motion.div 
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight mb-3" style={{ color: "#F4F1E8" }}>
              SELECT YOUR <span className="text-terracotta">CITY</span>
            </h2>
            <div className="w-12 h-0.5 mx-auto" style={{ background: "#D4AF37" }} />
          </motion.div>

          {/* Custom Dropdown - Fixed positioning for mobile */}
          <div style={{ position: 'relative', zIndex: 9999, overflow: 'visible' }}>
            <button
              onClick={() => setCityDropdownOpen(!cityDropdownOpen)}
              className="w-full py-4 px-5 rounded-lg text-left flex items-center justify-between transition-all duration-300"
              style={{
                background: '#111111',
                border: selectedCity ? '1px solid #D4AF37' : '1px solid rgba(255,255,255,0.1)',
              }}
              data-testid="city-dropdown"
            >
              <span className={selectedCity ? 'text-[#F4F1E8]' : 'text-white/40'}>
                {selectedCity || 'Choose a city...'}
              </span>
              <ChevronDown className={`w-5 h-5 text-white/40 transition-transform ${cityDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {cityDropdownOpen && (
              <motion.div 
                className="rounded-lg"
                style={{ 
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '8px',
                  background: '#111111', 
                  border: '1px solid rgba(255,255,255,0.15)',
                  zIndex: 9999,
                  boxShadow: '0 20px 50px rgba(0,0,0,0.9)',
                  overflow: 'hidden',
                }}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="max-h-[280px] overflow-y-auto">
                  {cities.map((city) => (
                    <button
                      key={city}
                      onClick={() => {
                        setSelectedCity(city);
                        setCityDropdownOpen(false);
                      }}
                      className="w-full py-4 px-5 text-left hover:bg-[#1A1A1A] transition-colors text-[#F4F1E8] border-b border-white/5 last:border-b-0"
                      data-testid={`city-option-${city.toLowerCase().replace(' ', '-')}`}
                    >
                      {city}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Package Interest Section */}
      <section 
        className="py-14 md:py-20 px-4"
        style={{ background: "#0A0A0A", position: "relative", zIndex: 10 }}
        data-testid="package-section"
      >
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(15,59,46,0.08) 0%, transparent 60%)',
          }}
        />

        <div className="max-w-2xl mx-auto relative z-10">
          <motion.div 
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight mb-3" style={{ color: "#F4F1E8" }}>
              WHAT ARE YOU <span className="text-[#D4AF37]">LOOKING FOR?</span>
            </h2>
            <div className="w-12 h-0.5 mx-auto" style={{ background: "#C24E2C" }} />
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {packageOptions.map((pkg, i) => (
              <motion.button
                key={pkg.id}
                onClick={() => togglePackage(pkg.id)}
                className={`py-4 px-5 rounded-lg text-left flex items-center gap-4 transition-all duration-300 ${
                  selectedPackages.includes(pkg.id) ? 'bg-[#1A1A1A]' : 'bg-[#111111] hover:bg-[#151515]'
                }`}
                style={{
                  border: selectedPackages.includes(pkg.id) ? '1px solid #D4AF37' : '1px solid rgba(255,255,255,0.05)',
                }}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                data-testid={`package-${pkg.id}`}
              >
                <div 
                  className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-all ${
                    selectedPackages.includes(pkg.id) ? 'bg-[#D4AF37]' : 'border border-white/20'
                  }`}
                >
                  {selectedPackages.includes(pkg.id) && <Check className="w-3 h-3 text-[#0B0B0F]" />}
                </div>
                <span className="text-[#F4F1E8] text-sm md:text-base">{pkg.label}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Message Field */}
      <section 
        className="py-14 md:py-20 px-4 relative"
        style={{ background: "#050505" }}
        data-testid="message-section"
      >
        <div className="max-w-2xl mx-auto relative z-10">
          <motion.div 
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight mb-3" style={{ color: "#F4F1E8" }}>
              TELL US WHAT YOU <span className="text-terracotta">HAVE IN MIND</span>
            </h2>
            <div className="w-12 h-0.5 mx-auto" style={{ background: "#D4AF37" }} />
          </motion.div>

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Birthday crew of 15. Want VIP + tequila tasting…"
            className="w-full h-32 md:h-40 bg-[#111111] border border-white/10 text-white placeholder:text-white/30 py-4 px-5 rounded-lg text-base resize-none focus:border-[#D4AF37] focus:outline-none transition-colors"
            data-testid="message-textarea"
          />
        </div>
      </section>

      {/* Contact Info Form */}
      <section 
        className="py-14 md:py-20 px-4 relative"
        style={{ background: "#0A0A0A" }}
        data-testid="contact-section"
      >
        {/* Top gold divider */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px" style={{ background: "#D4AF37" }} />

        <div className="max-w-lg mx-auto relative z-10">
          <motion.div 
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tight mb-3" style={{ color: "#F4F1E8" }}>
              YOUR <span className="text-[#D4AF37]">DETAILS</span>
            </h2>
            <div className="w-12 h-0.5 mx-auto" style={{ background: "#C24E2C" }} />
          </motion.div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="text"
              placeholder="Full Name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full bg-[#111111] border-white/10 text-white placeholder:text-white/30 py-5 px-5 rounded-lg focus:border-[#D4AF37]"
              required
              data-testid="input-name"
            />
            <Input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full bg-[#111111] border-white/10 text-white placeholder:text-white/30 py-5 px-5 rounded-lg focus:border-[#D4AF37]"
              required
              data-testid="input-email"
            />
            <Input
              type="tel"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full bg-[#111111] border-white/10 text-white placeholder:text-white/30 py-5 px-5 rounded-lg focus:border-[#D4AF37]"
              required
              data-testid="input-phone"
            />
            <Input
              type="text"
              placeholder="Group Size"
              value={formData.groupSize}
              onChange={(e) => setFormData({...formData, groupSize: e.target.value})}
              className="w-full bg-[#111111] border-white/10 text-white placeholder:text-white/30 py-5 px-5 rounded-lg focus:border-[#D4AF37]"
              data-testid="input-group-size"
            />

            {/* Submit Button */}
            <div className="pt-6">
              {error && (
                <p className="text-terracotta text-sm text-center mb-4" data-testid="vip-error">{error}</p>
              )}
              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-[#C9F400] hover:bg-[#D8FF40] text-[#0B0B0F] py-6 font-accent font-bold text-base tracking-wider rounded-lg transition-all hover:-translate-y-1 disabled:opacity-50"
                data-testid="submit-btn"
              >
                {loading ? "SUBMITTING..." : "REQUEST VIP ACCESS"}
              </Button>
              <p className="text-white/30 text-sm text-center mt-4">
                Our team will reach out within 24–48 hours.
              </p>
            </div>
          </form>
        </div>

        {/* Bottom gold divider */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-px" style={{ background: "#D4AF37" }} />
      </section>

      {/* Footer */}
      <footer 
        className="py-10 px-4 border-t border-white/5"
        style={{ background: "#050505" }}
        data-testid="vip-footer"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h4 className="font-heading font-bold text-lg mb-1" style={{ color: "#F4F1E8" }}>
            CINCO <span className="text-terracotta">DE</span> CRAWL
          </h4>
          <p className="text-white/30 text-xs mb-4">The Ultimate Bar Crawl Tour</p>
          <div className="w-8 h-0.5 mx-auto mb-4" style={{ background: "#1F4E3D" }} />
          <p className="text-white/20 text-xs">© 2026 Cinco De Crawl. Must be 21+.</p>
        </div>
      </footer>
    </div>
  );
};

export default VIPExperiences;
